import React from 'react';
import { Card, Typography, Grid, Stack, Skeleton, Alert, Button, Box, Chip, LinearProgress } from '@mui/material';
import Iconify from '../../iconify/Iconify';

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString()}`;

const Item = ({ label, value, highlight = false, color = 'text.primary' }) => (
  <Stack spacing={0.3}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant={highlight ? 'h6' : 'subtitle2'} sx={{ fontWeight: highlight ? 800 : 700, color }}>
      {value}
    </Typography>
  </Stack>
);

export default function BillingSummaryPanel({ data, loading, error, onRetry }) {
  const collectionRate = Number(data?.collectionRate || 0);
  const todayGenerated = Number(data?.todayBillsGenerated || 0);
  const todayPaid = Number(data?.todayBillsPaid || 0);
  const todayPending = Number(data?.todayBillsPending || 0);

  return (
    <Card
      sx={{
        p: 2.25,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 8px 22px rgba(16, 24, 40, 0.05)',
        height: '100%',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Iconify icon="solar:bill-list-bold-duotone" width={18} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Billing Summary
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: { xs: '0.72rem', md: '0.75rem' } }}>
        Revenue and collection snapshot
      </Typography>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Failed to load billing summary.
        </Alert>
      ) : loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
      ) : (
        <Stack spacing={1.1}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(25,118,210,0.12), rgba(46,125,50,0.08))',
              border: '1px solid rgba(25,118,210,0.18)',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Total Billed Amount
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.25rem', md: '1.45rem' } }}>
              {formatCurrency(data?.totalBilledAmount)}
            </Typography>
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Collection Progress
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {collectionRate.toFixed(2)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(collectionRate, 100))}
              sx={{ height: 8, borderRadius: 10 }}
            />
          </Box>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Item label="Bills Generated" value={Number(data?.billsGenerated || 0).toLocaleString()} />
            </Grid>
            <Grid item xs={6}>
              <Item label="Bills Paid" value={Number(data?.billsPaid || 0).toLocaleString()} color="success.main" />
            </Grid>
            <Grid item xs={6}>
              <Item label="Bills Pending" value={Number(data?.billsPending || 0).toLocaleString()} color="warning.main" />
            </Grid>
            <Grid item xs={6}>
              <Item label="Pending Amount" value={formatCurrency(data?.pendingAmount)} highlight color="error.main" />
            </Grid>
            <Grid item xs={6}>
              <Item label="Paid Amount" value={formatCurrency(data?.paidAmount)} color="success.main" />
            </Grid>
          </Grid>

          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Today Snapshot
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" color="info" label={`Generated: ${todayGenerated}`} />
              <Chip size="small" color="success" label={`Paid: ${todayPaid}`} />
              <Chip size="small" color="warning" label={`Pending: ${todayPending}`} />
            </Stack>
          </Box>
        </Stack>
      )}
    </Card>
  );
}
